import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsFromLine } from '@fortawesome/pro-solid-svg-icons';
import { NSTable } from 'bricks';
import { ICashFlowBudgetClassification, IPeriodData, IUsesActualsExpandedCategories } from 'views/ProFormaTable/types';
import RenderIf from 'components/RenderIf/RenderIf';
import { numeralFormatterCurrency } from 'ns_libs/formatter';
import { getProjectUsesActualsHeaders, getProjectUsesActualsTotals } from 'views/ProFormaTable/helpers';
import { Periods } from 'components/ProFormaReturnsTable/constant';
import { useUsesActualsTableContext } from './context/UsesTableActualsProvider';
import UsesTableHighLevelActuals from './UsesTableHighLevelActuals/UsesTableHighLevelActuals';
import './ProjectUsesActualsTable.scss';
import { useProFormaSocketContext } from '../../socketContext/ProFormaSocketProvider';

export interface IProjectUsesActualsTableProps {
    uses: ICashFlowBudgetClassification[];
}

const ProjectUsesActualsTable = ({ uses }: IProjectUsesActualsTableProps) => {
    const { periodCadence, showCents } = useProFormaSocketContext();

    const {
        hideAllActualsCategories, showAllActualsCategories, forecastMetric,
        isEarlyPlanningProject, selectedScenario,
    } = useUsesActualsTableContext();

    const [expandAllCategories, setExpandAllCategories] = useState(false);

    const actualsHeaders = getProjectUsesActualsHeaders(forecastMetric, isEarlyPlanningProject);
    const actualsTotals = getProjectUsesActualsTotals(forecastMetric, uses, isEarlyPlanningProject, selectedScenario);
    const showCentsFormat = showCents ? '$0,0.00' : '$0,0';

    const handleExpandAllCategories = () => {
        if (expandAllCategories) {
            hideAllActualsCategories();
            setExpandAllCategories(false);
        } else {
            const updatedExpandedCategories: IUsesActualsExpandedCategories = { highLevels: [], subcategories: [] };
            uses.forEach(highLevel => {
                updatedExpandedCategories.highLevels.push(highLevel.nodeName);
                (highLevel.children || []).forEach(subcategory => {
                    updatedExpandedCategories.subcategories.push(subcategory.nodeName);
                });
            });
            showAllActualsCategories(updatedExpandedCategories);
            setExpandAllCategories(true);
        }
    };

    let periods = [] as IPeriodData[];

    if (uses.length > 0) {
        periods = uses[0].periodData || [];

        if (periodCadence === Periods.ANNUALLY) {
            periods = uses[0].yearlyPeriodData || [];
        }
    }

    const calculatePeriodTotals = (budgetClassifications: ICashFlowBudgetClassification[] = []): number[] => {
        if (!budgetClassifications.length || !periods?.length) return [];

        const periodCount = periods.length;

        const totals = new Array(periodCount).fill(0);

        budgetClassifications.forEach(highLevel => {
            let highLevelPeriods = highLevel.periodData || [];
            if (periodCadence === Periods.ANNUALLY) {
                highLevelPeriods = highLevel.yearlyPeriodData || [];
            }
            highLevelPeriods.forEach((period, periodIndex) => {
                totals[periodIndex] += (+period.funding || 0);
            });
        });

        return totals;
    };

    const periodsTotals = calculatePeriodTotals(uses);

    return (
        <div className="d-flex scrollable-container pb-1">
            <NSTable
                className="NSTable--sticky-headers NSTable--sticky-headers--full-width
                    NSTable--sticky-headers--six-sticky-columns ProjectUsesActuals__table"
            >
                <thead className="NSTable__thead">
                    <tr className="NSTable__thead__tr">
                        <th className="NSTable__thead__tr__th NSTable__thead__tr__th--right-border align-middle py-1">
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center">
                                    <FontAwesomeIcon
                                        className="mr-2 text-muted cursor--pointer ReadOnlyWrapper--enable-pointer-events"
                                        icon={faArrowsFromLine}
                                        onClick={handleExpandAllCategories}
                                    />
                                    <span className="ml-0">Budget Line</span>
                                </div>
                            </div>
                        </th>
                        <th className="NSTable__thead__tr__th align-middle py-1">Progress</th>
                        <th className="NSTable__thead__tr__th align-middle text-right py-1">{actualsHeaders.totalBalanceHeaderName}</th>
                        <th className="NSTable__thead__tr__th align-middle text-right py-1">{actualsHeaders.balanceRemainingHeaderName}</th>
                        <th className="NSTable__thead__tr__th align-middle text-right py-1">Start date</th>
                        <th className="NSTable__thead__tr__th align-middle text-right py-1">End date</th>

                        {periods?.map((periodInfo, index) => (
                            <th
                                key={periodInfo.periodName}
                                className="NSTable__thead__tr__th align-middle
                                    NSTable__thead__tr__th--grey-bg NSTable__thead__tr__th--width-150 py-1"
                            >
                                <div className="h6 NSTable__thead__tr__th--grey-text text-uppercase text-nowrap text-right mb-0">
                                    {periodCadence === Periods.ANNUALLY ? `YEAR ${index}` : `MONTH ${index}`}
                                </div>
                                <div className="h6 text-uppercase text-right text-nowrap mt-1">
                                    {periodInfo.periodName}
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>

                <tbody className="NSTable__tbody">
                    <RenderIf isTrue={uses?.length}>
                        {uses?.map(highLevel => (
                            <UsesTableHighLevelActuals
                                key={highLevel.nodeName}
                                highLevel={highLevel}
                            />
                        ))}
                    </RenderIf>
                    <RenderIf isTrue={!uses?.length}>
                        <tr className="NSTable__tbody_tr NSTable__tbody__tr--level-1">
                            <td className="NSTable__tbody__tr__td align-middle" colSpan={6}>
                                <div className="text-center text-dark my-3">No project actuals data available</div>
                            </td>
                        </tr>
                    </RenderIf>
                </tbody>

                <tfoot className="NSTable__tfoot">
                    <tr className="NSTable__tfoot__tr">
                        <td className="NSTable__tfoot__tr__td NSTable__tfoot__tr__td--right-border text-left">
                            Grand total
                        </td>
                        <td className="NSTable__tfoot__tr__td" />
                        <td className="NSTable__tfoot__tr__td text-right text-nowrap">
                            {numeralFormatterCurrency(actualsTotals.totalBudget, showCentsFormat)}
                        </td>
                        <td className="NSTable__tfoot__tr__td text-right text-nowrap">
                            {numeralFormatterCurrency(actualsTotals.totalBalanceRemaining, showCentsFormat)}
                        </td>
                        <td className="NSTable__tfoot__tr__td" />
                        <td className="NSTable__tfoot__tr__td" />

                        {periodsTotals?.map((period, index) => (
                            /* eslint-disable-next-line react/no-array-index-key */
                            <td className="align-middle text-right" key={`${index}-${period}`}>
                                {numeralFormatterCurrency(period, showCentsFormat)}
                            </td>
                        ))}
                    </tr>
                </tfoot>
            </NSTable>
        </div>
    );
};

export default ProjectUsesActualsTable;
